import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Button, message, Modal, Dropdown, Popover, Avatar } from 'antd';
import { AWTemplateDto, AWTemplateStageDto } from '@api/Api';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as InfoSvg } from '@assets/icons/info.svg';
import { ReactComponent as UsersSvg } from '@assets/icons/approval-users-icon-completed.svg';
import { ReactComponent as CheckSvg } from '@assets/icons/check-mark-blue.svg';
import { useAWSDeleteTemplate } from '@hooks/queries/awTemplate';
import { hashedColor } from '@helpers/hashedColor';
import UserAvatar from '@components/UserAvatar';

import './TemplateBlock.less';

type TemplateBlockProps = {
  template: AWTemplateDto;
  selectedTemplate: AWTemplateDto | null;
  canManage: boolean;
  onEditTemplate: (value: AWTemplateDto) => void;
  setSelectedTemplate: (value: AWTemplateDto | null) => void;
};

function Stages({ stages }: { stages: AWTemplateStageDto[] }) {
  return (
    <>
      {stages.map((item, index) => {
        const borderColor = hashedColor(`${index + 1}`, 'taskGroup');
        return (
          <div key={item.id} className="template-block-stages">
            <div
              className="template-block-stages-line"
              style={{ backgroundColor: borderColor }}
            />
            <div className="template-block-stages-number">{index + 1}</div>
            <div className="template-block-stages-name">{item.name}</div>
            <div className="template-block-stages-users">
              <Avatar.Group
                maxCount={4}
                maxStyle={{
                  color: '#161819',
                  background: '#DBDBDB',
                  fontWeight: 700,
                  fontSize: 10,
                  lineHeight: 16
                }}
                size={18}
                maxPopoverTrigger="hover"
                maxPopoverPlacement="bottom"
              >
                {item.members.map((member) => {
                  return (
                    <UserAvatar
                      isActive={true}
                      key={member.email}
                      size={24}
                      src={member?.picture?.url || ''}
                      userEmail={member?.email || ''}
                      userName={member?.name || ''}
                    />
                  );
                })}
              </Avatar.Group>
            </div>
          </div>
        );
      })}
    </>
  );
}

export default function TemplateBlock({
  template,
  canManage,
  selectedTemplate,
  setSelectedTemplate,
  onEditTemplate
}: TemplateBlockProps) {
  const isSelected = selectedTemplate?.id === template.id;
  const stagesCount = template?.stages?.length;

  const uniqueMembersCount = useMemo(() => {
    if (!template.stages.length) return 0;
    const members = template.stages.flatMap((stage) => stage.members);
    const uniqueMembers = new Map(
      members.map((member) => [member.email, member])
    );
    return uniqueMembers.size;
  }, [template.stages]);

  const { mutateAsync: deleteTemplate, isPending: isLoadingDeleteTemplate } =
    useAWSDeleteTemplate({});

  const onDeleteTemplate = useCallback(
    (template: AWTemplateDto) => {
      if (isLoadingDeleteTemplate || !canManage) return;
      Modal.confirm({
        onOk: async () => {
          try {
            await deleteTemplate({
              id: template.id
            });
            if (isSelected) setSelectedTemplate(null);
          } catch (e: any) {
            message.error(e.description || 'Something went wrong');
          }
        },
        okButtonProps: {
          style: {
            minWidth: 140,
            marginLeft: 24
          }
        },
        cancelButtonProps: {
          type: 'text',
          style: {
            display: 'inline-flex'
          }
        },
        zIndex: 1011,
        width: 464,
        icon: null,
        centered: true,
        okText: 'Delete',
        cancelText: 'Cancel',
        closable: true,
        maskClosable: true,
        closeIcon: <CrossSvg />,
        content: (
          <div className="confirm-delete-modal">
            <div className="confirm-delete-modal_head">
              <InfoSvg />
              <h2 className="confirm-delete-modal_title">
                Are you sure you want to delete this template?
              </h2>
            </div>
            <p className="confirm-delete-modal_text">
              Once <strong>{template.name}</strong> template is deleted, it
              cannot be undone.
            </p>
          </div>
        )
      });
    },
    [
      canManage,
      isSelected,
      isLoadingDeleteTemplate,
      deleteTemplate,
      setSelectedTemplate
    ]
  );

  const menuItems: ItemType[] = useMemo(() => {
    return [
      {
        key: 'edit',
        label: 'Edit',
        onClick: (e) => {
          e.domEvent.stopPropagation();
          onEditTemplate(template);
        }
      },
      {
        key: 'delete',
        label: 'Delete',
        onClick: (e) => {
          e.domEvent.stopPropagation();
          onDeleteTemplate(template);
        }
      }
    ];
  }, [onDeleteTemplate, onEditTemplate, template]);

  return (
    <div
      className={classNames('template-block', {
        'b-selected': isSelected
      })}
      onClick={() => setSelectedTemplate(isSelected ? null : template)}
    >
      <div className="template-block-head">
        <div className="template-block-icon">
          <CheckSvg />
        </div>
        <div className="template-block-name">{template.name}</div>
        <Dropdown
          menu={{
            items: menuItems,
            style: {
              minWidth: 100
            }
          }}
          placement="bottomLeft"
          trigger={['click']}
          disabled={!canManage}
        >
          <Button
            type="text"
            size="small"
            style={{ padding: 0 }}
            loading={isLoadingDeleteTemplate}
            className="template-block-head-action-btn"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionSvg style={{ marginRight: 0 }} />
          </Button>
        </Dropdown>
      </div>
      {!!stagesCount && (
        <Popover
          overlayClassName="template-block-stage-popover"
          content={<Stages stages={template.stages} />}
          placement="bottom"
        >
          <div className="template-block-stage">
            {stagesCount} stage{stagesCount > 1 && 's'}
            {!!uniqueMembersCount && (
              <div className="template-block-stage-members-count">
                <UsersSvg />
                {uniqueMembersCount}
              </div>
            )}
          </div>
        </Popover>
      )}
    </div>
  );
}
